/**
 * Supply "Elevated" primitives — docs/supply-design/elevated/README.md (the
 * six moves, approved 2026-08-23). Every Supply floor surface composes its
 * screen from THESE so the count overview, order, receive, waste, transfer
 * and the landing read as one system:
 *
 *   1. One HeroCard per screen holds the job (progress · next · primary).
 *   2. Numbers are the hierarchy — Numeral 40/800 tabular, 28px title,
 *      12px labels. Three sizes per screen.
 *   3. One elevation level: the HeroCard carries the two-layer shadow;
 *      everything else is flat.
 *   4. State is shape — StatusChip, ListRow `disabled` dims (opacity .7)
 *      instead of a warning well.
 *   5. Chrome earns its height — ChipRow is ONE scrollable line, never a
 *      wrap; TextLinkRow is one 44px line of secondary actions.
 *   6. Motion with restraint — Rise: 350ms staggered rise, reduced-motion off.
 *
 * Order (polish PR4) added the list-and-cart grammar: ItemRow (64px, free
 * trailing slot) + ADD_WELL / InlineStepper, StatusRow (one 48px toned
 * line), SegmentedControl/Segment (44px chip a server form can post) and
 * SearchField (48px, 16px input). ListRow takes `href` for a navigating row.
 *
 * Styling is tokens only (supply-theme.css; the hero shadow is `--shadow-hero`,
 * defined in both palettes). The two literal values in this file are the
 * ChipRow right-edge fade gradient (mask-image) and the active Segment's
 * 1px lift (`0 1px 2px rgba(34,34,46,0.08)`, from the artboard). PRIMARY/SECONDARY
 * button classes stay in ds/buttons.ts — this kit does not fork them.
 */
"use client";

import { Children, type ButtonHTMLAttributes, type CSSProperties, type ReactNode } from "react";
import Link from "next/link";
import { Check, ChevronRight, Minus, Search } from "lucide-react";

/** Two-layer shadow from the README — the ONLY elevation on a floor screen. */
export const HERO_SHADOW = "shadow-[var(--shadow-hero)]";

/**
 * A state hue on a card is a WASH, never an edge rule. The coloured left /
 * top stripe (`border-l-4 border-l-[var(--danger)]`) was banned across
 * /supply on 2026-08-30 — it reads as AI-template chrome whichever edge it
 * sits on (docs/visual-style-spec.md "Edge stripes"; guard:
 * app/supply/__tests__/edge-stripe-ban.test.ts). Identity lives IN the card:
 * ~10% of the hue over the surface, and the headline numeral in the full hue
 * (`<Numeral tone>`). Semantic tones are EARNED by data — write
 * `tone={open > 0 ? "danger" : "success"}`, never a hardcoded "danger";
 * amber is the one decorative hue.
 */
export const TONE_WASH = {
  danger: "supply-tone-danger",
  success: "supply-tone-success",
  warning: "supply-tone-warning",
  info: "supply-tone-info",
  amber: "supply-tone-amber",
} as const;
export type Tone = keyof typeof TONE_WASH;
const TONE_INK: Record<Tone, string> = {
  danger: "text-[var(--danger)]",
  success: "text-[var(--success)]",
  warning: "text-[var(--warning)]",
  info: "text-[var(--info)]",
  amber: "text-[var(--accent-text)]",
};

export function HeroCard({ children, className = "", label, tone }: { children: ReactNode; className?: string; label?: string; /** Hue wash over the surface — see TONE_WASH. */ tone?: Tone }) {
  return <section aria-label={label} className={`flex flex-col gap-3.5 rounded-[18px] border border-[var(--border-soft)] ${tone ? TONE_WASH[tone] : "bg-[var(--surface)]"} p-[18px] ${HERO_SHADOW} ${className}`}>{children}</section>;
}

/**
 * 40px (hero) or 20px (sticky header) tabular numeral with an optional
 * faint "/ N" suffix. The number IS the hierarchy — no label inside.
 */
export function Numeral({ value, of, size = 40, className = "", tone }: { value: ReactNode; of?: ReactNode; size?: 40 | 20; className?: string; /** The numeral in the full hue — the pair of a HeroCard `tone` wash. */ tone?: Tone }) {
  const big = size === 40;
  return <span className={`flex items-baseline gap-1 tabular-nums ${className}`}>
    <strong className={`${big ? "text-[40px] tracking-[-0.03em]" : "text-[20px] tracking-[-0.02em]"} font-extrabold leading-none ${tone ? TONE_INK[tone] : "text-[var(--text)]"}`}>{value}</strong>
    {of !== undefined && of !== null ? <span className={`${big ? "text-[18px]" : "text-[14px]"} font-bold text-[var(--faint)]`}>/ {of}</span> : null}
  </span>;
}

const CHIP_TONES = {
  live: "bg-[var(--success-well)] text-[var(--success)]",
  muted: "bg-[var(--raised)] text-[var(--muted)]",
  accent: "bg-[var(--amber-well)] text-[var(--accent-text)]",
  info: "bg-[var(--info-well)] text-[var(--info)]",
} as const;
export type StatusChipTone = keyof typeof CHIP_TONES;

/** 24px pill; `dot` paints the 6px live marker (Sedang dikira). */
export function StatusChip({ tone, dot = false, children, className = "" }: { tone: StatusChipTone; dot?: boolean; children: ReactNode; className?: string }) {
  return <span className={`inline-flex h-6 items-center gap-1.5 rounded-full px-2.5 text-[12px] font-bold ${CHIP_TONES[tone]} ${className}`}>{dot ? <span aria-hidden className="size-1.5 rounded-full bg-current" /> : null}{children}</span>;
}

/** 12px/700 uppercase tracked section label with an optional right caption. */
export function SectionLabel({ children, caption, className = "" }: { children: ReactNode; caption?: ReactNode; className?: string }) {
  return <div className={`flex items-center justify-between gap-2 px-0.5 ${className}`}>
    <h2 className="text-[12px] font-bold uppercase tracking-[0.06em] text-[var(--muted)]">{children}</h2>
    {caption ? <span className="text-[12px] font-semibold text-[var(--faint)]">{caption}</span> : null}
  </div>;
}

/**
 * 56px chooser row: leading badge slot (a COUNT or a glyph — never a
 * decorative index), 15px title, one-line detail, trailing chevron.
 * `disabled` = the door is closed for a reason the screen states (dims to
 * opacity .7 + aria-disabled, tap does nothing); `pending` = an action is in
 * flight (disabled without the dim semantics).
 */
export function ListRow({ badge, badgeTone = "muted", title, detail, disabled = false, pending = false, onClick, href, isStatic = false, className = "" }: { badge?: ReactNode; /** `accent` = work already started here (a half-received delivery) keeps its amber cue below the fold. */ badgeTone?: "muted" | "accent" | "success"; title: ReactNode; detail?: ReactNode; disabled?: boolean; pending?: boolean; onClick?: () => void; /** A navigating row renders as a Link instead of a button (order's supplier picker). */ href?: string; /** A row with NO door (transfer's Keluar rows when the note flag is off): a plain div, no chevron — never a dead button wearing one. */ isStatic?: boolean; className?: string }) {
  const shape = `flex min-h-14 w-full items-center gap-3 rounded-[14px] border border-[var(--border-soft)] bg-[var(--surface)] px-3.5 py-2 text-left ${disabled ? "opacity-70" : "disabled:opacity-50"} ${className}`;
  const body = <>
    {badge !== undefined ? <span className={`flex size-7 shrink-0 items-center justify-center rounded-[8px] text-[12px] font-extrabold tabular-nums ${badgeTone === "accent" ? "bg-[var(--amber-well)] text-[var(--accent-text)]" : badgeTone === "success" ? "bg-[var(--success-well)] text-[var(--success)]" : "bg-[var(--raised)] text-[var(--muted)]"}`}>{badge}</span> : null}
    <span className="min-w-0 flex-1"><strong className="block truncate text-[15px] font-bold text-[var(--text)]">{title}</strong>{detail ? <span className="mt-0.5 block truncate text-[11px] font-bold leading-4 text-[var(--muted)]">{detail}</span> : null}</span>
    {isStatic ? null : <ChevronRight className="size-4 shrink-0 text-[var(--faint)]" aria-hidden />}
  </>;
  if (isStatic) return <div className={shape}>{body}</div>;
  if (href && !disabled && !pending) return <Link href={href} className={shape}>{body}</Link>;
  return <button type="button" disabled={disabled || pending} aria-disabled={disabled || undefined} onClick={onClick} className={shape}>{body}</button>;
}

/**
 * 64px item row (Order Elevated): 40px thumbnail slot, 15px/700 single-line
 * title (identity may truncate), 12px detail of at most two facts, and a
 * free trailing slot (an add well, a stepper). A plain div on purpose — the
 * trailing slot holds the buttons, so the row itself cannot be one.
 * `active` = the amber focus ring (the row being edited); `dimmed` = not
 * orderable for a reason the detail states.
 */
export function ItemRow({ thumb, title, detail, trailing, active = false, dimmed = false, testId, className = "", onClick }: { thumb?: ReactNode; title: ReactNode; detail?: ReactNode; trailing?: ReactNode; active?: boolean; dimmed?: boolean; testId?: string; className?: string; /** A SELECTABLE row (waste's picker): the thumb+text body becomes a 64px <button> firing this; the trailing well stays as the visible affordance. */ onClick?: () => void }) {
  const body = <>
    <span aria-hidden className="flex size-10 shrink-0 items-center justify-center rounded-[10px] bg-[var(--raised)] text-[var(--muted)]">{thumb}</span>
    <span className="min-w-0 flex-1"><strong className="block truncate text-[15px] font-bold text-[var(--text)]">{title}</strong>{detail ? <span className="mt-0.5 flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[12px] font-semibold leading-4 text-[var(--muted)]">{detail}</span> : null}</span>
  </>;
  return <div data-testid={testId} className={`flex min-h-16 items-center gap-3 rounded-[14px] border border-[var(--border-soft)] bg-[var(--surface)] py-2 pl-3.5 pr-2.5 ${active ? "ring-2 ring-[var(--amber)]" : ""} ${dimmed ? "opacity-70" : ""} ${className}`}>
    {onClick ? <button type="button" data-testid={testId ? `${testId}-body` : undefined} onClick={onClick} className="-my-2 -ml-3.5 flex min-h-16 min-w-0 flex-1 items-center gap-3 py-2 pl-3.5 text-left">{body}</button> : body}
    {trailing}
  </div>;
}

/** The 44×44 amber add well that sits in an ItemRow's trailing slot. */
export const ADD_WELL = "flex size-11 shrink-0 items-center justify-center rounded-[12px] bg-[var(--amber-well)] text-[22px] font-bold leading-none text-[var(--accent-text)] active:scale-[0.97]";

/**
 * −/value/+ at 44px each for a row that already holds a quantity. The
 * caller owns the aria-labels (they must name the step the tap will move)
 * and the value node (packs over base, or base over packs).
 */
export function InlineStepper({ value, onDecrement, onIncrement, decrementLabel, incrementLabel, disabled = false, className = "" }: { value: ReactNode; onDecrement: () => void; onIncrement: () => void; decrementLabel: string; incrementLabel: string; disabled?: boolean; className?: string }) {
  return <span className={`flex shrink-0 items-center gap-1 ${className}`}>
    <button type="button" disabled={disabled} onClick={onDecrement} aria-label={decrementLabel} className="flex size-11 items-center justify-center rounded-[12px] border border-[var(--border)] bg-[var(--surface)] text-[18px] leading-none text-[var(--body)] active:scale-[0.97] disabled:opacity-50">−</button>
    <span className="min-w-11 text-center tabular-nums">{value}</span>
    <button type="button" disabled={disabled} onClick={onIncrement} aria-label={incrementLabel} className={`${ADD_WELL} text-[20px]`}>+</button>
  </span>;
}

const ROW_TONES = {
  danger: "bg-[var(--danger-well)] text-[var(--danger)]",
  accent: "bg-[var(--amber-well)] text-[var(--accent-text)]",
  info: "bg-[var(--info-well)] text-[var(--info)]",
  success: "bg-[var(--success-well)] text-[var(--success)]",
  muted: "bg-[var(--raised)] text-[var(--muted)]",
} as const;
export type StatusRowTone = keyof typeof ROW_TONES;

/**
 * One 48px status line (README move 4 — state is shape, not a card): 8px
 * dot, 13px/700 text in the tone, an optional trailing action (a 44px text
 * button or link) or a chevron when the row itself navigates.
 */
export function StatusRow({ tone, children, detail, trailing, href, className = "" }: { tone: StatusRowTone; children: ReactNode; /** A SECOND, wrapping line (a rejection reason) — a sentence, so it never truncates; the row grows past 48px to hold it. */ detail?: ReactNode; trailing?: ReactNode; href?: string; className?: string }) {
  const shape = `flex items-center gap-2.5 rounded-[14px] px-3.5 ${detail ? "py-2" : "min-h-12"} ${ROW_TONES[tone]} ${className}`;
  const body = <>
    <span aria-hidden className="size-2 shrink-0 rounded-full bg-current" />
    <span className="min-w-0 flex-1">
      <span className="block truncate text-[13px] font-bold">{children}</span>
      {detail ? <span className="mt-0.5 block whitespace-normal text-[12px] font-semibold leading-[1.4]">{detail}</span> : null}
    </span>
    {href ? <ChevronRight className="size-4 shrink-0 opacity-60" aria-hidden /> : trailing}
  </>;
  return href ? <Link href={href} className={shape}>{body}</Link> : <div className={shape}>{body}</div>;
}

/**
 * A 56px checklist row (Landing Elevated, "Minggu percubaan"): a 22px check
 * circle leading (success-filled when done, hollow when owed, a faint dash
 * when zero is fine), a 15px title that is the row's door (min 44px tap
 * target as a Link when `href` is given), and a free trailing slot for ONE
 * 13px text action. Done rows mute + strike the title — state is shape.
 */
export function CheckRow({ state, title, href, trailing, testId, className = "", ...data }: { state: "done" | "todo" | "neutral"; title: ReactNode; href?: string; trailing?: ReactNode; testId?: string; className?: string; [key: `data-${string}`]: string | undefined }) {
  const mark = state === "done"
    ? <span data-state="done" aria-hidden className="flex size-[22px] shrink-0 items-center justify-center rounded-full bg-[var(--success-well)] text-[var(--success)]"><Check className="size-3" strokeWidth={3} /></span>
    : state === "neutral"
      ? <span data-state="neutral" aria-hidden className="flex size-[22px] shrink-0 items-center justify-center rounded-full bg-[var(--raised)] text-[var(--faint)]"><Minus className="size-3" strokeWidth={3} /></span>
      : <span data-state="todo" aria-hidden className="size-[22px] shrink-0 rounded-full border-2 border-[var(--border)]" />;
  const titleClass = `flex min-h-11 min-w-0 flex-1 items-center text-[15px] ${state === "done" ? "font-semibold text-[var(--muted)] line-through" : "font-bold text-[var(--text)]"}`;
  return <div data-testid={testId} {...data} className={`flex min-h-14 items-center gap-3 rounded-[14px] border border-[var(--border-soft)] bg-[var(--surface)] px-3.5 py-1 ${className}`}>
    {mark}
    {href ? <Link href={href} className={titleClass}><span className="min-w-0 leading-snug">{title}</span></Link> : <span className={titleClass}><span className="min-w-0 leading-snug">{title}</span></span>}
    {trailing}
  </div>;
}

/** Class for the text action in a CheckRow's trailing slot — 44px tall, 13px accent. */
export const CHECK_ROW_ACTION = "inline-flex min-h-11 shrink-0 items-center text-[13px] font-bold text-[var(--accent-text)]";

/** Class for the text action inside a StatusRow's trailing slot. */
export const STATUS_ROW_ACTION = "inline-flex min-h-11 shrink-0 items-center text-[13px] font-bold underline-offset-2 disabled:opacity-60";

/**
 * 44px segmented chip (the order's CK | Pembekal switch): a raised track
 * with 4px padding, the active segment lifted white. Segments are plain
 * buttons so a server form can post them (name/value) and a client can
 * onClick them alike.
 */
export function SegmentedControl({ label, children, className = "" }: { label?: string; children: ReactNode; className?: string }) {
  return <div role="group" aria-label={label} className={`flex h-11 shrink-0 items-center rounded-[12px] bg-[var(--raised)] p-1 ${className}`}>{children}</div>;
}

export function Segment({ active, className = "", children, ...rest }: { active: boolean } & ButtonHTMLAttributes<HTMLButtonElement>) {
  return <button type="button" aria-pressed={active} {...rest} className={`relative flex h-full items-center rounded-[9px] px-3 text-[12px] font-bold transition before:absolute before:inset-x-0 before:top-1/2 before:h-11 before:-translate-y-1/2 before:content-[''] disabled:opacity-100 ${active ? "bg-[var(--surface)] text-[var(--accent-text)] shadow-[0_1px_2px_rgba(34,34,46,0.08)]" : "text-[var(--muted)]"} ${className}`}>{children}</button>;
}

/**
 * 48px search field for a sticky head. The input stays at 16px — a focused
 * field under 16px makes iOS zoom the page and keep it zoomed.
 */
export function SearchField({ value, onChange, placeholder, label, className = "" }: { value: string; onChange: (next: string) => void; placeholder: string; label: string; className?: string }) {
  return <label className={`flex h-12 items-center gap-2.5 rounded-[14px] border border-[var(--border)] bg-[var(--surface)] px-3.5 focus-within:border-[var(--accent)] ${className}`}>
    <Search className="size-[18px] shrink-0 text-[var(--muted)]" strokeWidth={2.4} aria-hidden />
    <input type="search" value={value} onChange={(event) => onChange(event.target.value)} placeholder={placeholder} aria-label={label} className="h-full w-full min-w-0 bg-transparent text-[16px] font-semibold text-[var(--text)] placeholder:text-[var(--faint)] focus:outline-none" />
  </label>;
}

/** Accent fill on a raised track; the fill never disappears (min-width 8px). */
export function ProgressBar({ value, max, size = 8, className = "" }: { value: number; max: number; size?: 8 | 6; className?: string }) {
  const pct = max > 0 ? Math.round((Math.min(value, max) / max) * 100) : 0;
  return <div role="progressbar" aria-valuemin={0} aria-valuemax={max} aria-valuenow={value} className={`${size === 8 ? "h-2" : "h-1.5"} overflow-hidden rounded-full bg-[var(--raised)] ${className}`}>
    <div className="h-full min-w-2 rounded-full bg-[var(--accent)]" style={{ width: `${pct}%` }} />
  </div>;
}

/**
 * ONE scrollable line of chips (README move 5) — wrapping would cost the
 * hero a row per overflow. Scrollbar hidden; chips never shrink.
 */
export function ChipRow({ children, size = 28, className = "" }: { children: ReactNode; size?: 28 | 32; className?: string }) {
  return <div data-chip-size={size} className={`flex gap-1.5 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden [mask-image:linear-gradient(to_right,#000_calc(100%-24px),transparent)] [-webkit-mask-image:linear-gradient(to_right,#000_calc(100%-24px),transparent)] ${className}`}>{children}</div>;
}

/** A chip inside ChipRow. `active` = amber-well (accent) or ink (tone "ink"). */
export function Chip({ active = false, activeTone = "accent", size = 28, pressed, disabled = false, children, className = "", onClick }: { active?: boolean; activeTone?: "accent" | "ink"; size?: 28 | 32; /** aria-pressed for a toggle chip (the list's axis switch). */ pressed?: boolean; /** Tappable chip only: dims (opacity .7, README move 4) and ignores taps while a resolve is in flight. */ disabled?: boolean; children: ReactNode; className?: string; onClick?: () => void }) {
  const tone = active ? (activeTone === "ink" ? "bg-[var(--text)] text-[var(--surface)]" : "bg-[var(--amber-well)] text-[var(--accent-text)]") : "bg-[var(--raised)] text-[var(--body)]";
  const shape = `inline-flex ${size === 32 ? "h-8 px-3" : "h-7 px-2.5"} shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full text-[12px] font-bold tabular-nums ${tone} ${className}`;
  // Tappable chips (PR3 sticky header) keep the 44px floor as the BUTTON
  // around the 28/32px pill (the look is the span, the target is the
  // button); a plain span carries no hit target.
  return onClick ? <button type="button" aria-pressed={pressed} disabled={disabled} onClick={onClick} className="-my-1.5 flex min-h-11 shrink-0 items-center disabled:opacity-70"><span className={shape}>{children}</span></button> : <span className={shape}>{children}</span>;
}

/** Class for an action inside TextLinkRow — 44px tall, text only. */
export const TEXT_LINK = "inline-flex min-h-11 items-center disabled:opacity-60";

/** One 44px line of secondary text actions, a 1px divider between each. */
export function TextLinkRow({ children, className = "" }: { children: ReactNode; className?: string }) {
  const items = Children.toArray(children).filter(Boolean);
  return <div className={`flex items-center justify-center gap-4 text-[13px] font-bold text-[var(--muted)] ${className}`}>
    {items.flatMap((item, index) => index === 0 ? [item] : [<span key={`divider-${index}`} aria-hidden className="h-3.5 w-px bg-[var(--border)]" />, item])}
  </div>;
}

/**
 * A counted row collapsed to its number (README move 4, PR3): 52px bar,
 * 22px/800 tabular value, 13px unit, faint "· detail", and ONE text action on
 * the right (Betulkan) — non-destructive by contract; the destructive
 * gesture never lives here.
 */
export function SummaryBar({ value, unit, detail, action, className = "" }: { value: ReactNode; unit?: ReactNode; detail?: ReactNode; action?: { label: ReactNode; onClick: () => void; disabled?: boolean }; className?: string }) {
  return <div className={`flex min-h-[52px] items-center justify-between gap-3 rounded-[12px] border border-[var(--border-soft)] bg-[var(--inner)] px-3.5 ${className}`}>
    <span className="flex min-w-0 flex-wrap items-baseline gap-1.5 tabular-nums">
      <strong className="text-[22px] font-extrabold leading-none text-[var(--text)]">{value}</strong>
      {unit ? <span className="text-[13px] font-bold text-[var(--muted)]">{unit}</span> : null}
      {detail ? <span className="text-[13px] font-semibold text-[var(--faint)]">· {detail}</span> : null}
    </span>
    {action ? <button type="button" disabled={action.disabled} onClick={action.onClick} className="min-h-11 shrink-0 text-[13px] font-bold text-[var(--accent-text)] disabled:opacity-60">{action.label}</button> : null}
  </div>;
}

const FIELD_TONES = {
  accent: "bg-[var(--amber-well)] text-[var(--accent-text)]",
  info: "bg-[var(--info-well)] text-[var(--info)]",
} as const;

/**
 * A 52px entry field shell with its unit fused on as a solid block (the
 * approved "unit lives ON the control" pattern): amber = pack counting,
 * info-blue = base-unit weighing. The caller owns the <input>; this owns the
 * frame, the focus ring colour and the dimmed state.
 */
export function UnitField({ unit, tone, dimmed = false, children, className = "" }: { unit: ReactNode; tone: keyof typeof FIELD_TONES; dimmed?: boolean; children: ReactNode; className?: string }) {
  return <div className={`flex h-[52px] min-w-0 flex-1 items-stretch overflow-hidden rounded-[12px] border border-[var(--border)] bg-[var(--surface)] focus-within:border-[var(--accent)] focus-within:ring-1 focus-within:ring-[var(--accent)] ${dimmed ? "opacity-60" : ""} ${className}`}>
    {children}
    <span className={`flex shrink-0 items-center px-3 text-[12px] font-extrabold uppercase tracking-[0.04em] ${FIELD_TONES[tone]}`}>{unit}</span>
  </div>;
}

/** The <input> class that goes inside UnitField — 20px/800, accent caret. */
export const UNIT_FIELD_INPUT = "h-full w-full min-w-0 flex-1 bg-transparent px-3 text-[20px] font-extrabold tabular-nums text-[var(--text)] caret-[var(--accent)] outline-none placeholder:text-[14px] placeholder:font-semibold placeholder:text-[var(--faint)] disabled:text-[var(--faint)]";

/**
 * The load rise: `.supply-rise` (supply-theme.css) at 0/50/100/150ms via
 * `--rise-delay`. `step` 0–3 (0/50/100/150ms); reduced-motion users see no animation.
 */
export function Rise({ step = 0, as: Tag = "div", children, className = "", label }: { step?: 0 | 1 | 2 | 3; as?: "div" | "section" | "header"; children: ReactNode; className?: string; label?: string }) {
  return <Tag aria-label={label} className={`supply-rise ${className}`} style={{ "--rise-delay": `${step * 50}ms` } as CSSProperties}>{children}</Tag>;
}
